import i18n from "../../i18n";
import {
	IMPORT_DISMISS,
	DELETE_JOB_DISMISS,
	USAGES_JOB_DISMISS,
	COPY_JOB_DISMISS,
	MPP_IMPORT_DISMISS,
	AI_IMPORT_DISMISS,
	SCENARIO_DELETE_JOB_DISMISS,
	AUTO_FEED_ACTUALS_JOB_DISMISS,
	RESET_CAPACITY_JOB_DISMISS,
	SCOPE_CSV_IMPORT_JOB_DISMISS,
	GENERATIVE_JOB_DISMISS,
	PORTFOLIO_SIM_JOB_DISMISS,
	PORTFOLIO_COPY_JOB_DISMISS,
} from "../types";

/**
 * Table of every async job kind the ProgressNotificationStack surfaces. Keep
 * this in lock-step with the per-kind WebSocket services that dispatch into
 * each slice and the i18n keys under `progressNotifications.kinds.*`.
 *
 * Adding a new kind: append an entry here, create the matching Redux slice +
 * dismiss action TYPE constant, and add the i18n strings. The stack will pick
 * it up automatically — no UI change required.
 *
 * We import the dismiss action TYPE strings rather than the action creators so
 * Jest unit tests don't need to evaluate the full action-creator module tree
 * (which transitively pulls in Bryntum ESM via Scheduler utils).
 */
export const KINDS = [
	{ kind: "import",         stateKey: "import",         dismissType: IMPORT_DISMISS },
	{ kind: "delete",         stateKey: "delete",         dismissType: DELETE_JOB_DISMISS },
	{ kind: "updateUsages",   stateKey: "updateUsages",   dismissType: USAGES_JOB_DISMISS },
	{ kind: "copy",           stateKey: "scenarioCopy",   dismissType: COPY_JOB_DISMISS },
	{ kind: "mppBatchImport", stateKey: "mppBatchImport", dismissType: MPP_IMPORT_DISMISS },
	{ kind: "aiImport",       stateKey: "aiImport",       dismissType: AI_IMPORT_DISMISS },
	{ kind: "scenarioDelete", stateKey: "scenarioDelete", dismissType: SCENARIO_DELETE_JOB_DISMISS },
	{ kind: "autoFeedActuals",       stateKey: "autoFeedActuals",       dismissType: AUTO_FEED_ACTUALS_JOB_DISMISS },
	{ kind: "resetCapacity",         stateKey: "resetCapacity",         dismissType: RESET_CAPACITY_JOB_DISMISS },
	{ kind: "generativeScheduling",  stateKey: "generativeScheduling",  dismissType: GENERATIVE_JOB_DISMISS },
	{ kind: "portfolioSimulation",   stateKey: "portfolioSimulation",   dismissType: PORTFOLIO_SIM_JOB_DISMISS },
	{ kind: "portfolioCopy",         stateKey: "portfolioCopy",         dismissType: PORTFOLIO_COPY_JOB_DISMISS },
	{ kind: "scopeCsvImport",         stateKey: "structureCsvImport",    dismissType: SCOPE_CSV_IMPORT_JOB_DISMISS },
];

/**
 * Resolve the label + target strings shown on a card. Reads from i18n with a
 * sensible English fallback so missing FR keys don't show "[missing key]".
 *
 * @param {"running"|"done"|"warning"|"error"} state
 * @param {string} kind
 * @param {object} slice  the Redux state for this kind
 */
export function buildCardCopy(state, kind, slice) {
	const t = i18n.t.bind(i18n);
	const total = slice.totalProjects || 0;
	const fileName = slice.fileName || null;
	const failed = slice.failedCount || 0;

	const k = `progressNotifications.kinds.${kind}`;
	// Generative scheduling runs distinct operations (scopes / assumptions / strategy /
	// removal); show a step-specific message instead of a generic one.
	const op = kind === "generativeScheduling" ? slice.operation : null;
	const label = (() => {
		if (state === "running") {
			if (op) return t(`${k}.running_${op}`, generativeRunning(op));
			return t(`${k}.running`, defaultRunning(kind));
		}
		if (state === "error") return t(`${k}.errorTitle`, defaultErrorTitle(kind));
		if (state === "warning") {
			return t(`${k}.warningTitle`, {
				n: failed,
				defaultValue: defaultWarningTitle(kind, failed),
			});
		}
		if (op) return t(`${k}.done_${op}`, generativeDone(op));
		return t(`${k}.done`, { n: total, defaultValue: defaultDone(kind, total) });
	})();

	const target = buildTarget(kind, slice, fileName, total, failed, state);
	return { label, target };
}

// Portfolio simulation runs ONE combined PDM solve across every selected
// project (shared resources/assumptions) — the per-project rows in the card
// are progress reporting on that single run, not independent simulations.
// Without this hint, seeing "Project A: done, Project B: pending" reads as
// parallel independent runs, which is the "misleading" report this addresses.
export function combinedSimulationHint(kind, state, total) {
	if (kind !== "portfolioSimulation" || state !== "running" || total <= 1) return null;
	return i18n.t("progressNotifications.kinds.portfolioSimulation.combinedRunHint", {
		n: total,
		defaultValue: `Combined simulation across ${total} projects — shared resources and assumptions, so progress isn't independent per project.`,
	});
}

function buildTarget(kind, slice, fileName, total, failed, state) {
	const t = i18n.t.bind(i18n);
	// Partial success: the headline says what failed, the target says what
	// worked — the pair reads "Import finished — 2 failed / 4 of 6 succeeded".
	// Denominator falls back to the processed count when the total never
	// arrived (dropped QUEUED frame).
	if (state === "warning") {
		const completed = slice.completedCount || 0;
		const denominator = total > 0 ? total : completed + failed;
		if (denominator > 0) {
			return t("progressNotifications.target.partial", {
				completed,
				total: denominator,
				defaultValue: `${completed} of ${denominator} succeeded`,
			});
		}
	}
	if (state === "error" && failed > 0 && total > 0) {
		return t("progressNotifications.target.failedOf", {
			failed,
			total,
			defaultValue: `${failed} of ${total} failed`,
		});
	}
	if (fileName) return fileName;
	if (kind === "delete" || kind === "import" || kind === "mppBatchImport") {
		return t("progressNotifications.target.projects", {
			n: total,
			defaultValue: `${total} ${i18n.t("term.project").toLowerCase()}${total === 1 ? "" : "s"}`,
		});
	}
	if (kind === "scenarioDelete" || kind === "copy" || kind === "updateUsages" || kind === "autoFeedActuals") {
		const first = slice.projects?.[0]?.name || slice.projects?.[0]?.label;
		if (total === 1 && first) return first;
		return t("progressNotifications.target.scenarios", {
			n: total,
			defaultValue: `${total} scenario${total === 1 ? "" : "s"}`,
		});
	}
	if (kind === "aiImport") {
		// fileName already handled above; if we're here it was null.
		return "schedule";
	}
	if (kind === "resetCapacity") {
		return t("progressNotifications.target.resources", {
			n: total,
			defaultValue: `${total} resource${total === 1 ? "" : "s"}`,
		});
	}
	if (kind === "generativeScheduling") {
		// Name the scenario rather than saying "schedule": with several runs in the
		// dock, a generic target line makes it impossible to tell which one is which.
		if (slice.scenarioName) return slice.scenarioName;
		if (slice.scenarioId != null) {
			return t("progressNotifications.target.scenarioById", {
				id: slice.scenarioId,
				defaultValue: `Scenario #${slice.scenarioId}`,
			});
		}
		return t("progressNotifications.target.schedule", { defaultValue: "schedule" });
	}
	return "";
}

function generativeRunning(op) {
	switch (op) {
		case "scopes":
			return "Updating scopes";
		case "assumptions":
			return "Updating assumptions";
		case "strategy":
			return "Updating strategy";
		case "removal":
			return "Removing and recomputing";
		case "benchmarkKeep":
			return "Creating a scenario from the benchmark";
		case "benchmarkReplace":
			return "Applying the benchmark durations";
		case "benchmarkUndo":
			return "Undoing the benchmark durations";
		default:
			return "Generating schedule";
	}
}

function generativeDone(op) {
	switch (op) {
		case "scopes":
			return "Scopes updated";
		case "assumptions":
			return "Assumptions updated";
		case "strategy":
			return "Strategy updated";
		case "removal":
			return "Removed, schedule recomputed";
		case "benchmarkKeep":
			return "Scenario created from the benchmark";
		case "benchmarkReplace":
			return "Benchmark durations applied";
		case "benchmarkUndo":
			return "Benchmark durations undone";
		default:
			return "Schedule generated";
	}
}

function defaultRunning(kind) {
	switch (kind) {
		case "import":
			return `Importing ${i18n.t("term.project").toLowerCase()}s`;
		case "delete":
			return `Deleting ${i18n.t("term.project").toLowerCase()}s`;
		case "updateUsages":
			return "Updating usages";
		case "copy":
			return "Copying scenarios";
		case "mppBatchImport":
			return `Learning from past ${i18n.t("term.project").toLowerCase()}s`;
		case "aiImport":
			return "Importing & enriching";
		case "scenarioDelete":
			return "Deleting scenarios";
		case "autoFeedActuals":
			return "Auto-feeding actuals across approved scenarios";
		case "resetCapacity":
			return "Resetting capacity";
		case "generativeScheduling":
			return "Generating schedule";
		case "portfolioSimulation":
			return "Running simulation";
		case "portfolioCopy":
			return "Creating portfolio scenario";
		case "scopeCsvImport":
			return "Importing scope library";
		default:
			return "Working";
	}
}

function defaultDone(kind, n) {
	const verb = (past, noun) => `${past} ${n} ${noun}${n === 1 ? "" : "s"}`;
	switch (kind) {
		case "import":
			return verb("Imported", i18n.t("term.project").toLowerCase());
		case "delete":
			return verb("Deleted", i18n.t("term.project").toLowerCase());
		case "updateUsages":
			return "Usages updated";
		case "copy":
			return verb("Copied", "scenario");
		case "mppBatchImport":
			return verb("Learned from", i18n.t("term.project").toLowerCase());
		case "aiImport":
			return "Import & enrichment complete";
		case "scenarioDelete":
			return verb("Deleted", "scenario");
		case "autoFeedActuals":
			return `Auto-fed actuals into ${n} scenario${n === 1 ? "" : "s"}`;
		case "resetCapacity":
			return `Reset ${n} resource${n === 1 ? "" : "s"}`;
		case "generativeScheduling":
			return "Schedule generated";
		case "portfolioSimulation":
			return "Simulation complete";
		case "portfolioCopy":
			return "Portfolio scenario created";
		case "scopeCsvImport":
			return "Scope library imported";
		default:
			return "Done";
	}
}

// Partial-success headline: names the finished action AND the failure count,
// so "44 of 47 worked" never reads as a total failure.
function defaultWarningTitle(kind, n) {
	const suffix = `${n} failed`;
	switch (kind) {
		case "import":
			return `Import finished — ${suffix}`;
		case "delete":
			return `${i18n.t("term.project")} delete finished — ${suffix}`;
		case "updateUsages":
			return `Usages updated — ${suffix}`;
		case "copy":
			return `Scenario copy finished — ${suffix}`;
		case "mppBatchImport":
			return `Learning finished — ${suffix}`;
		case "aiImport":
			return `Import & enrich finished — ${suffix}`;
		case "scenarioDelete":
			return `Scenario delete finished — ${suffix}`;
		case "autoFeedActuals":
			return `Auto-feed finished — ${suffix}`;
		case "resetCapacity":
			return `Capacity reset finished — ${suffix}`;
		case "generativeScheduling":
			return `Schedule generation finished — ${suffix}`;
		case "portfolioSimulation":
			return `Simulation finished — ${suffix}`;
		case "portfolioCopy":
			return `Portfolio scenario created — ${suffix}`;
		case "scopeCsvImport":
			return `Scope import finished — ${suffix}`;
		default:
			return `Finished — ${suffix}`;
	}
}

function defaultErrorTitle(kind) {
	switch (kind) {
		case "import":
			return "Excel import failed";
		case "delete":
			return `${i18n.t("term.project")} delete failed`;
		case "updateUsages":
			return "Update usages failed";
		case "copy":
			return "Scenario copy failed";
		case "mppBatchImport":
			return `Learning from past ${i18n.t("term.project").toLowerCase()}s failed`;
		case "aiImport":
			return "Import & enrich failed";
		case "scenarioDelete":
			return "Scenario delete failed";
		case "autoFeedActuals":
			return "Auto-feed actuals failed";
		case "resetCapacity":
			return "Capacity reset failed";
		case "generativeScheduling":
			return "Schedule generation failed";
		case "portfolioSimulation":
			return "Simulation failed";
		case "portfolioCopy":
			return "Portfolio scenario creation failed";
		case "scopeCsvImport":
			return "Scope import failed";
		default:
			return "Failed";
	}
}
